import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  TextField, Container, Typography, CircularProgress, Box, Alert, Button, CssBaseline, 
  Paper, ThemeProvider, createTheme, InputAdornment, IconButton
} from '@mui/material';
import { createTheme as createMuiTheme } from '@mui/material/styles';
import CommonAppBar from '../components/CommonAppBar';
import Footer from './Footer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import './styles.css';

const AccountActivation = () => {
    const { activationId, activationToken } = useParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();

    const defaultTheme = createMuiTheme({
        palette: {
            primary: {
                main: 'rgb(18,82,166)',
            },
            background: {
                default: '#f8fafc',
            },
        },
        typography: {
            fontFamily: '"Times New Roman", Times, serif',
        },
    });

    useEffect(() => {
        if (showSuccess) {
            setTimeout(() => {
                navigate('/login');
            }, 5000); // Redirect after 5 seconds
        }
    }, [showSuccess, navigate]);

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleToggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const activateAccount = async () => {
        if (!password || !confirmPassword) {
            setError('Please fill in all password fields.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true); // Start loading
        try {
            const response = await axios.post(`https://ngat.ethernet.edu.et/api/activate/${activationId}/${activationToken}`, { password });
            setShowSuccess(true); // Show success message
            setError(''); // Clear previous errors
        } catch (error) {
            setError(error.response.data.message || 'Failed to activate account');
            setShowSuccess(false);
        } finally {
            setLoading(false); // Stop loading regardless of the outcome
        }
    };

    return (
        <ThemeProvider theme={defaultTheme}>
            <CssBaseline />
            <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
                {/* Global Mobile TextField Fixes */}
                <style>
                    {`
                        @media (max-width: 600px) {
                            .MuiTextField-root .MuiOutlinedInput-root {
                                min-height: 48px !important;
                            }
                            .MuiTextField-root .MuiInputLabel-root {
                                line-height: 1.2 !important;
                            }
                            .MuiTextField-root .MuiOutlinedInput-notchedOutline {
                                border-radius: 8px !important;
                            }
                            .MuiTextField-root .MuiOutlinedInput-input {
                                padding: 12px 14px !important;
                            }
                        }
                    `}
                </style>
                
                {/* Background Effects - Same as Login */}
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: `
                                radial-gradient(circle at 20% 80%, rgba(18,82,166,0.08) 0%, transparent 50%),
                                radial-gradient(circle at 80% 20%, rgba(18,82,166,0.08) 0%, transparent 50%),
                                radial-gradient(circle at 40% 40%, rgba(18,82,166,0.06) 0%, transparent 50%)
                            `,
                            pointerEvents: 'none'
                        }
                    }}
                />
                
                {/* Connected Particle Network Effect */}
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `
                            radial-gradient(2px 2px at 20px 30px, rgba(18,82,166,0.15), transparent),
                            radial-gradient(2px 2px at 40px 70px, rgba(18,82,166,0.12), transparent),
                            radial-gradient(1px 1px at 90px 40px, rgba(18,82,166,0.1), transparent),
                            radial-gradient(1px 1px at 130px 80px, rgba(18,82,166,0.08), transparent),
                            radial-gradient(2px 2px at 160px 30px, rgba(18,82,166,0.15), transparent),
                            radial-gradient(2px 2px at 200px 90px, rgba(18,82,166,0.12), transparent),
                            radial-gradient(1px 1px at 240px 50px, rgba(18,82,166,0.1), transparent),
                            radial-gradient(1px 1px at 280px 120px, rgba(18,82,166,0.08), transparent),
                            radial-gradient(2px 2px at 320px 40px, rgba(18,82,166,0.15), transparent),
                            radial-gradient(2px 2px at 360px 100px, rgba(18,82,166,0.12), transparent),
                            radial-gradient(1px 1px at 400px 60px, rgba(18,82,166,0.1), transparent),
                            radial-gradient(1px 1px at 440px 140px, rgba(18,82,166,0.08), transparent),
                            radial-gradient(2px 2px at 480px 30px, rgba(18,82,166,0.15), transparent),
                            radial-gradient(2px 2px at 520px 80px, rgba(18,82,166,0.12), transparent),
                            radial-gradient(1px 1px at 560px 50px, rgba(18,82,166,0.1), transparent),
                            radial-gradient(1px 1px at 600px 110px, rgba(18,82,166,0.08), transparent),
                            radial-gradient(2px 2px at 640px 70px, rgba(18,82,166,0.15), transparent),
                            radial-gradient(2px 2px at 680px 130px, rgba(18,82,166,0.12), transparent),
                            radial-gradient(1px 1px at 720px 40px, rgba(18,82,166,0.1), transparent),
                            radial-gradient(1px 1px at 760px 90px, rgba(18,82,166,0.08), transparent),
                            radial-gradient(2px 2px at 800px 60px, rgba(18,82,166,0.15), transparent),
                            radial-gradient(2px 2px at 840px 120px, rgba(18,82,166,0.12), transparent),
                            radial-gradient(1px 1px at 880px 80px, rgba(18,82,166,0.1), transparent),
                            radial-gradient(1px 1px at 920px 140px, rgba(18,82,166,0.08), transparent),
                            radial-gradient(2px 2px at 960px 50px, rgba(18,82,166,0.15), transparent),
                            radial-gradient(2px 2px at 1000px 100px, rgba(18,82,166,0.12), transparent),
                            radial-gradient(1px 1px at 1040px 70px, rgba(18,82,166,0.1), transparent),
                            radial-gradient(1px 1px at 1080px 130px, rgba(18,82,166,0.08), transparent),
                            radial-gradient(2px 2px at 1120px 40px, rgba(18,82,166,0.15), transparent),
                            radial-gradient(2px 2px at 1160px 90px, rgba(18,82,166,0.12), transparent),
                            radial-gradient(1px 1px at 1200px 60px, rgba(18,82,166,0.1), transparent),
                            radial-gradient(1px 1px at 1240px 110px, rgba(18,82,166,0.08), transparent)
                        `,
                        backgroundSize: '1280px 160px',
                        backgroundRepeat: 'repeat',
                        pointerEvents: 'none',
                        zIndex: 0
                    }}
                />
                
                {/* Molecular Structure */}
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `
                            radial-gradient(4px 4px at 80px 80px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 240px 240px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 400px 400px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 560px 560px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 720px 720px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 880px 880px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 1040px 1040px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 1200px 1200px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 1360px 1360px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 1520px 1520px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 1680px 1680px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 1840px 1840px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 2000px 2000px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 2160px 2160px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 2320px 2320px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 2480px 2480px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 2640px 2640px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 2800px 2800px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 2960px 2960px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 3120px 3120px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 3280px 3280px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 3440px 3440px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 3600px 3600px, rgba(18,82,166,0.2), transparent),
                            radial-gradient(4px 4px at 3760px 3760px, rgba(18,82,166,0.2), transparent)
                        `,
                        backgroundSize: '3840px 3840px',
                        backgroundRepeat: 'repeat',
                        pointerEvents: 'none',
                        zIndex: 0
                    }}
                />
                
                {/* Grid Structure */}
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `
                            linear-gradient(rgba(18,82,166,0.04) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(18,82,166,0.04) 1px, transparent 1px)
                        `,
                        backgroundSize: '60px 60px',
                        pointerEvents: 'none',
                        zIndex: 0
                    }}
                />
                
                {/* Main Content */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <CommonAppBar 
                        title="Ministry of Education"
                        showHomeButton={true}
                        showContactButton={true}
                        showLoginButton={true}
                        customButtons={[
                            {
                                label: 'Register',
                                onClick: () => navigate('/registration')
                            }
                        ]}
                    />

                    <Box sx={{ 
                        minHeight: 'calc(100vh - 140px)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 4
                    }}>
                        <Container component="main" sx={{ maxWidth: 'sm', px: { xs: 2, md: 3 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                
                                {/* Header Section */}
                                <Box sx={{ textAlign: 'center', mb: 4, width: '100%' }}>
                                    <Typography variant="h5" sx={{ mb: 1, color: '#000000', fontWeight: 700, letterSpacing: '-0.5px' }}>
                                        National Graduate Admission Test (NGAT)
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.6, maxWidth: 480, mx: 'auto' }}>
                                        Activate your account by setting a secure password to complete your registration process.
                    </Typography>
                                </Box>

                                {/* Main Form Card */}
                                <Paper elevation={0} sx={{ 
                                    width: '100%', maxWidth: 480, p: { xs: 2, md: 4 }, backgroundColor: '#ffffff',
                                    borderRadius: { xs: 2, md: 3 }, border: '1px solid #e2e8f0',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                    position: 'relative', overflow: 'hidden'
                                }}>
                                    {/* Decorative accent */}
                                    <Box sx={{
                                        position: 'absolute', top: 0, left: 0, right: 0, height: 4,
                                        background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                                    }} />
                                    
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="h5" sx={{ mb: 3, color: '#1e293b', fontWeight: 600, textAlign: 'center' }}>
                    Set Your Password
                </Typography>

                                        <Box component="form">
                <TextField
                                                variant="outlined" margin="normal" required fullWidth
                                                name="password" label="New Password"
                                                type={showPassword ? 'text' : 'password'}
                                                id="password" autoComplete="new-password"
                                                value={password} onChange={handlePasswordChange} disabled={loading}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '&:hover fieldset': { borderColor: 'rgb(18,82,166)' },
                                                        '&.Mui-focused fieldset': { borderColor: 'rgb(18,82,166)' },
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                                        '&.Mui-focused': { color: 'rgb(18,82,166)' },
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                                        padding: { xs: '12px 14px', md: '16px 14px' },
                                                    },
                                                }}
                                            />
                                            
                <TextField
                                                variant="outlined" margin="normal" required fullWidth
                                                name="confirmPassword" label="Confirm Password"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                id="confirmPassword" autoComplete="new-password"
                                                value={confirmPassword} onChange={handleConfirmPasswordChange} disabled={loading}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={handleToggleConfirmPasswordVisibility} edge="end">
                                                                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '&:hover fieldset': { borderColor: 'rgb(18,82,166)' },
                                                        '&.Mui-focused fieldset': { borderColor: 'rgb(18,82,166)' },
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                                        '&.Mui-focused': { color: 'rgb(18,82,166)' },
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                                        padding: { xs: '12px 14px', md: '16px 14px' },
                                                    },
                                                }}
                                            />

                                            {error && (
                                                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                                                    {error}
                                                </Alert>
                                            )}

                                            {showSuccess && (
                                                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                                                    Account successfully activated! Redirecting to login...
                                                </Alert>
                                            )}

                <Button
                                                type="button" fullWidth variant="contained" onClick={activateAccount} disabled={loading || showSuccess}
                                                sx={{
                                                    mt: 3, mb: 2, py: 1.5, backgroundColor: 'rgb(18,82,166)',
                                                    '&:hover': { backgroundColor: 'rgb(14,65,130)' },
                                                    '&:disabled': { background: '#e2e8f0', color: '#94a3b8' },
                                                    fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 600,
                                                    textTransform: 'none', borderRadius: 2,
                                                }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={24} sx={{ color: 'white', mr: 1 }} />
                                                        Activating account...
                        </Box>
                    ) : showSuccess ? 'Account Activated' : 'Activate Account'}
                </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>
            </Container>
                    </Box>
                </Box>
            </div>
            <Footer />
        </ThemeProvider>
    );
};

export default AccountActivation;
